import * as React from 'react';
import { isEmpty, isNil, all } from 'ramda';

import { IUseSimpleFormParams } from './types/public';
import { TErrorsState, TValue, TValuesState } from './types/internal';
import { getErrorsFromFields } from './utils/getErrorsFromFields';
import { isEmptyString } from '../validators';

/**
 * Хук поставляет набор необходимой логики для формы
 * Используется в шагах опросника, где есть поля ввода. Управляет логикой валидации и временного хранения значений
 * */
export const useSimpleForm = <T = TValue>(params: IUseSimpleFormParams<T>) => {
  const { initialValues, validators, onSubmit, required = false } = params;
  const [isFormEmpty, setIsFormEmpty] = React.useState(isEmpty(initialValues));
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(!required);
  const [errors, setErrors] = React.useState<TErrorsState>({});
  const [values, setValues] = React.useState<TValuesState<T>>(initialValues || {});
  const isSubmitDisabled = isFormEmpty ? required : isSubmitted && !isFormValid;

  const handleChange = React.useCallback((key: string, value: T) => {
    setIsSubmitted(false);
    setErrors(prevState => ({ ...prevState, [key]: '' }));
    setValues(prevState => ({ ...prevState, [key]: value }));
  }, []);

  React.useEffect(() => {
    if (validators) {
      const newErrorsObj = getErrorsFromFields({ values, validators });

      setIsFormValid(isEmpty(newErrorsObj));
    }
    setIsFormEmpty(all((value: unknown) => isEmptyString(value) || isNil(value), Object.values(values)));
  }, [values, validators]);

  const handleValidate = React.useCallback(
    (valuesToValidate: TValuesState<T>) => {
      if (validators) {
        const newErrorsObj = getErrorsFromFields({ values: valuesToValidate, validators });

        const isCurrentFormValid = isEmpty(newErrorsObj);

        setErrors(newErrorsObj);
        setIsFormValid(isCurrentFormValid);

        return { newErrorsObj, isCurrentFormValid };
      }

      return { isCurrentFormValid: true };
    },
    [validators],
  );

  const handleSubmit = React.useCallback(() => {
    const { isCurrentFormValid } = handleValidate(values);

    setIsSubmitted(true);

    if (isCurrentFormValid) {
      onSubmit(values);
    }
  }, [handleValidate, values, onSubmit]);

  React.useEffect(() => {
    if (initialValues && !isEmpty(initialValues)) {
      handleValidate(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { handleChange, handleSubmit, errors, values, isFormEmpty, isFormValid, isSubmitDisabled };
};
